import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import FieldSet from 'flarum/common/components/FieldSet';
import Switch from 'flarum/common/components/Switch';
import SEASONS, { easterSunday } from '../common/seasons';

/**
 * Seasonal decorations. The active season is set as data-bd-season on
 * <html>, which the season's stylesheet (less/forum/seasons/) keys on, and
 * its moving pieces (snow, bats, fireworks, …) go in a fixed layer over the
 * page (.BdSeason, styled in less/forum/seasons.less).
 *
 * Which season: in "auto" mode the first enabled one whose window holds the
 * visitor's date, in "manual" mode the chosen one, in "off" mode none.
 * Members can turn decorations off for themselves in their settings.
 */

/** Numbers that look random but are the same on every page load. */
function stableRandom(seed) {
  let state = seed;
  return () => (state = (state * 9301 + 49297) % 233280) / 233280;
}

/** Pieces falling down the page (snow, petals, leaves, confetti). */
function fall(count, seed, size, duration = [9, 18]) {
  const random = stableRandom(seed);
  let html = '';
  for (let i = 0; i < count; i++) {
    const style = [`--x:${(random() * 100).toFixed(1)}%`, `--s:${Math.round(size[0] + random() * (size[1] - size[0]))}px`, `--d:${(duration[0] + random() * (duration[1] - duration[0])).toFixed(1)}s`,
    // A negative delay starts each piece part-way down, not all at the top.
    `--delay:-${(random() * duration[1]).toFixed(1)}s`, `--sway:${Math.round(10 + random() * 30)}px`, `--r:${Math.round(random() * 360)}deg`].join(';');
    html += `<div class="BdSeason-fall BdSeason-fall--${i % 3}" style="${style}"><i></i></div>`;
  }
  return html;
}
/** Things flying across now and then (bats, a sleigh, gulls, butterflies). */
function fly(flights, wings) {
  return flights.map(({
    top,
    w,
    h,
    d,
    delay
  }) => `<div class="BdSeason-fly${wings ? ` BdSeason-fly--${wings}` : ''}" style="--top:${top}px;--w:${w}px;--h:${h}px;--d:${d}s;--delay:${delay}s"><i></i></div>`).join('');
}
/** Fireworks over the top of the page. */
function bursts(list) {
  return list.map(({
    x,
    y,
    s,
    hue,
    d,
    delay
  }) => `<div class="BdSeason-burst" style="--x:${x}%;--y:${y}px;--s:${s}px;--hue:${hue}deg;--d:${d}s;--delay:${delay}s"></div>`).join('');
}
const PEEK = '<div class="BdSeason-peek"><i></i></div>';
const HOP = '<div class="BdSeason-hop"><i></i></div>';

// What each season puts over the page. Its stylesheet gives the pieces
// their look.
const LAYERS = {
  halloween: () => fly([{
    top: 110,
    w: 52,
    h: 22,
    d: 26,
    delay: 3
  }, {
    top: 190,
    w: 38,
    h: 16,
    d: 33,
    delay: 11
  }, {
    top: 72,
    w: 30,
    h: 13,
    d: 39,
    delay: 19
  }], 'flap') + PEEK,
  christmas: () => fall(26, 7, [3, 9]) + fly([{
    top: 70,
    w: 64,
    h: 51,
    d: 52,
    delay: 14
  }]) + PEEK,
  newyear: () => fall(26, 11, [8, 14], [7, 13]),
  easter: () => fly([{
    top: 120,
    w: 34,
    h: 25,
    d: 34,
    delay: 4
  }, {
    top: 210,
    w: 28,
    h: 21,
    d: 42,
    delay: 15
  }, {
    top: 80,
    w: 24,
    h: 18,
    d: 50,
    delay: 27
  }], 'flutter') + HOP,
  spring: () => fall(14, 3, [12, 20], [12, 22]),
  summer: () => fly([{
    top: 90,
    w: 44,
    h: 18,
    d: 40,
    delay: 6
  }, {
    top: 150,
    w: 32,
    h: 13,
    d: 52,
    delay: 22
  }], 'flap'),
  autumn: () => fall(14, 5, [16, 26], [11, 20]),
  winter: () => fall(22, 13, [3, 9])
};

// What each season puts over the top of the page only, scrolling away
// with the hero.
const SKIES = {
  newyear: () => bursts([
  // Mostly over the open right of the hero, away from the title.
  {
    x: 60,
    y: 70,
    s: 110,
    hue: 0,
    d: 7,
    delay: 0.5
  }, {
    x: 74,
    y: 60,
    s: 140,
    hue: 200,
    d: 9,
    delay: 2.5
  }, {
    x: 8,
    y: 190,
    s: 80,
    hue: 290,
    d: 8,
    delay: 5
  }, {
    x: 86,
    y: 150,
    s: 100,
    hue: 120,
    d: 10,
    delay: 7
  }])
};

/** MM-DD as a comparable number (10-20 → 1020); the fallback if malformed. */
function monthDay(value, fallback) {
  const match = /^(\d{1,2})-(\d{1,2})$/.exec(String(value ?? '').trim()) || /^(\d{1,2})-(\d{1,2})$/.exec(fallback);
  return Number(match[1]) * 100 + Number(match[2]);
}

/** Whether a day falls in Easter week: Palm Sunday to Easter Monday. */
function isEaster(today) {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const easter = easterSunday(today.getFullYear()).getTime();
  const DAY = 24 * 60 * 60 * 1000;

  // Rounded: a day is 23 or 25 hours long when the clocks change.
  const offset = Math.round((day - easter) / DAY);
  return offset >= -7 && offset <= 1;
}
export function activeSeason(today = new Date()) {
  const mode = app.forum.attribute('bimmerdockSeasonsMode') || 'auto';
  if (mode === 'off') return null;
  if (mode === 'manual') {
    const chosen = app.forum.attribute('bimmerdockSeasonsManual');
    return SEASONS.some(season => season.key === chosen) ? chosen : null;
  }
  const now = (today.getMonth() + 1) * 100 + today.getDate();
  for (const season of SEASONS) {
    if (!app.forum.attribute(`bimmerdockSeason_${season.key}`)) continue;
    if (!season.start || !season.end) {
      if (season.key === 'easter' && isEaster(today)) return season.key;
      continue;
    }
    const start = monthDay(app.forum.attribute(`bimmerdockSeason_${season.key}_start`), season.start);
    const end = monthDay(app.forum.attribute(`bimmerdockSeason_${season.key}_end`), season.end);

    // A window can run over New Year (12-27 to 01-02).
    if (start <= end ? now >= start && now <= end : now >= start || now <= end) return season.key;
  }
  return null;
}
function memberWantsDecorations() {
  return app.session.user?.preferences()?.bdSeasonalDecorations !== false;
}
let layers = [];
function addLayer(className, html) {
  const layer = document.createElement('div');
  layer.className = className;
  layer.setAttribute('aria-hidden', 'true');
  layer.innerHTML = html;
  document.body.appendChild(layer);
  layers.push(layer);
}
export function applySeason() {
  const season = memberWantsDecorations() ? activeSeason() : null;
  if (season) {
    document.documentElement.dataset.bdSeason = season;
  } else {
    delete document.documentElement.dataset.bdSeason;
  }
  layers.forEach(layer => layer.remove());
  layers = [];
  if (!season) return;
  if (LAYERS[season]) addLayer(`BdSeason BdSeason--${season}`, LAYERS[season]());
  if (SKIES[season]) addLayer(`BdSeason BdSeason--sky BdSeason--${season}`, SKIES[season]());
}
let saving = false;

// The settings page is loaded lazily, so it is patched when it registers.

export default function seasons() {
  app.beforeMount(applySeason);
  extend('flarum/forum/components/SettingsPage', 'settingsItems', function (items) {
    const user = this.user;
    if (!user) return;
    items.add('bdSeasonal', m(FieldSet, {
      className: "Settings-bdSeasonal FieldSet--min",
      label: app.translator.trans('bimmerdock-theme.forum.settings.seasonal_heading')
    }, m(Switch, {
      state: user.preferences()?.bdSeasonalDecorations !== false,
      loading: saving,
      onchange: value => {
        saving = true;
        user.savePreferences({
          bdSeasonalDecorations: value
        }).then(() => {
          saving = false;
          applySeason();
          m.redraw();
        });
      }
    }, app.translator.trans('bimmerdock-theme.forum.settings.seasonal_label'), m("span", {
      className: "helpText"
    }, app.translator.trans('bimmerdock-theme.forum.settings.seasonal_help')))), 45);
  });
}